import * as React from 'react';
import { Field, useField, useFormikContext } from 'formik';
import { useTranslation } from 'next-i18next';
import ControlInput from 'ap-admin-components/lib/Components/ControlInput';
import FormRow from 'ap-admin-components/lib/Components/FormRow';
import Form from 'ap-admin-components/lib/Components/Form';
import FormColumn from 'ap-admin-components/lib/Components/FormColumn';
import ControlFiles from 'ap-admin-components/lib/Components/ControlFiles';
import useUpload, { UploadFolder } from '@/lib/hooks/useUpload';
import useOverlayActions from '@/lib/hooks/useOverlayActions';
import { OverlayType } from '@/lib/enums/OverlayType.enum';
import OverlayFileUpdateConfirm from '@/components/overlay/OverlayFileUpdateConfirm';
import { getOverlayNameWithKey } from '@/lib/helpers/overlay';

interface IProps {
  name: string;
  label: string;
  disabled?: boolean;
  folder?: UploadFolder;
  layout?: 'dialog';
}

const FileUploadField: React.FC<IProps> = ({ name, label, disabled, folder, layout }) => {
  const { t } = useTranslation();
  const { isSubmitting } = useFormikContext();
  const { open } = useOverlayActions();
  const [, , { setValue }] = useField(name);

  const { accept, upload, existing, loading } = useUpload({
    onCompleted: ({ url }) => void setValue(url),
    onExisting: (fileName) => open(getOverlayNameWithKey(OverlayType.file_update_confirm, fileName)),
    folder,
  });

  const onFiles = (accepted: File[]) => {
    if (loading || disabled || isSubmitting) {
      return;
    }
    const file = accepted[0];
    upload({ file });
  };

  return (
    <Form>
      {layout === 'dialog' ? (
        <React.Fragment>
          <Field
            name={name}
            placeholder={label}
            as={ControlInput}
            disabled={disabled || isSubmitting}
          />
          <ControlFiles
            accept={accept}
            onFilesAdded={onFiles}
            buttonProps={{
              title: t('upload.start'),
              icon: 'document',
              intent: 'discrete',
              loading,
            }}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <FormColumn>
            <FormRow label={label}>
              <Field
                name={name}
                as={ControlInput}
                disabled={disabled || isSubmitting}
              />
            </FormRow>
          </FormColumn>
          <FormColumn>
            <FormRow>
              <ControlFiles
                accept={accept}
                onFilesAdded={onFiles}
                buttonProps={{
                  title: t('upload.start'),
                  icon: 'document',
                  intent: 'discrete',
                  loading,
                }}
              />
            </FormRow>
          </FormColumn>
        </React.Fragment>
      )}

      <OverlayFileUpdateConfirm
        file={existing.file}
        onConfirm={existing.update}
        onCancel={existing.clear}
      />
    </Form>
  );
};

export default FileUploadField;
